import { getDisplayDate } from "../../../shared/scripts/date-functions";
import { UserAffiliationSelctor } from "./special-case-sections/UserAffiliationSelection";
import { PathogenFocusSelctor } from "./special-case-sections/PathognSelection";
import { UserPermissionSelctor } from "./special-case-sections/UserPermissionSelection";
import { UserGroupSelector } from "./special-case-sections/UserGroupSelection";
import { CenterCoreProjectSelection } from "./special-case-sections/CenterCoreProjectSelection";
import { ListSelection } from "./special-case-sections/ListSelection";
import { FileUpload } from "./special-case-sections/FileUpload";
import { UserAccessSelector } from "./special-case-sections/UserAccessSelection";
import {DeliverableTrlDropdown, DeliverableStageSelection, DeliverableSubcategorySelection } from "./special-case-sections/DeliverablesSelection";
import _ from 'lodash';

import { JsonItem, PickListItem, RevamppVirus, RevamppCore, RevamppProject, AppUserAffiliation } from "./list-management.objects";

export class Section {
    id: string;
    title: string;
    description: string;
    fields: SectionField[];
    icon: string;
    visibleEditMultiple: boolean;
    noChangeLog: boolean;

    constructor(id: string, title: string, fields: SectionField[], description: string = "", icon: string = "", visibleEditMultiple: boolean = true, noChangeLog: boolean = false) {
        this.id = id;
        this.title = title;
        this.fields = fields;
        this.description = description;
        if (icon) { this.icon = icon }
        else { this.icon = "fa-solid fa-pen-to-square" }
        this.visibleEditMultiple = visibleEditMultiple;
        this.noChangeLog = noChangeLog;
    }

    generateHtml(displayFull: boolean = true): string {
        let html = `<div id=${this.id} class="mb-4">`;
        html += `<h6 class="section-title">${this.title}</h6>`;
        html += `<div class="edit-section-block">`

        this.fields.forEach(field => {
            html += field.generateHtml(displayFull);
        });

        html += "</div></div>";
        return html;
    }
}

export class SectionField {
    id: string;
    displayName: string;
    type: string;
    propertyName: string;
    options: string[][]; // compoundOptions
    pickListName: string;
    storedPickList: PickListItem[];

    description: string = "";
    isRequired: boolean = false;
    isReadOnly: boolean = false;
    alwaysValidate: boolean = false;

    storedValues: any[];
    initialValue: any;

    // Special Cases
    specialCaseEntity: SpecialCaseEntity;

    // For filter dropdowns (options change based on other dropdown)
    filterParentId: string;
    filterPropertyName: string;
    filterListName: string;
    filterListIdProperty: string;

    constructor(id: string, displayName: string, type: string, propertyName: string, isRequired: boolean = false, pickListName: string = "", description: string = "", options: string[][] = undefined, additionalPickLists: string[] = []) {
        this.id = id;
        this.displayName = displayName;
        this.type = type;
        this.propertyName = propertyName;
        this.isRequired = isRequired;
        this.description = description;
        this.options = options;
        this.pickListName = pickListName;
        this.storedPickList = [];
        

        if (this.type == "pathogen-focus-selection" || this.type == "pathogen-focus-selection-no-family") {
            let showVirusFamilyBtn = true;
            if (this.type == "pathogen-focus-selection-no-family") {
                showVirusFamilyBtn = false;
            }
            this.specialCaseEntity = new PathogenFocusSelctor(id, propertyName, pickListName, additionalPickLists, displayName, isRequired, showVirusFamilyBtn);
        }

        if (this.type == "affiliation-selection") {
            this.specialCaseEntity = new UserAffiliationSelctor(id, propertyName, pickListName, additionalPickLists, displayName, isRequired);
        }

        if (this.type.includes("permissions-selection")) {
            let isNew = this.type.includes("new");
            this.specialCaseEntity = new UserPermissionSelctor(id, propertyName, pickListName, displayName, isRequired, isNew);
        }

        if (this.type == "group-selection") {
            this.specialCaseEntity = new UserGroupSelector(id, propertyName, pickListName, additionalPickLists, displayName, isRequired);
        }

        if (this.type == "group-selection-teams") {
            this.specialCaseEntity = new UserGroupSelector(id, propertyName, pickListName, additionalPickLists, displayName, isRequired, true);
        }

        if (this.type == "access-selection") {
            this.specialCaseEntity = new UserAccessSelector(id, propertyName, pickListName, additionalPickLists, displayName, isRequired);
        }

        if (this.type.includes("deliverable-trl-dropdown") || this.type.includes("deliverable-subcategory-dropdown") || this.type.includes("deliverable-stage-dropdown")) {
            let categoryDropdownArr: string[] = [];
            options.forEach(option => {
                if (option[0]) categoryDropdownArr.push(option[0]);
            });
            if (categoryDropdownArr.length > 0) {
                let categoryDropdownId = categoryDropdownArr.find(item => item.includes("category"));
                let isNew = this.type.includes("new");
                if (categoryDropdownId) {
                    if (this.type.includes("deliverable-trl-dropdown")) {
                        this.specialCaseEntity = new DeliverableTrlDropdown(id, propertyName, pickListName, displayName, description, categoryDropdownId, isNew);
                    }
                    else if (this.type.includes("deliverable-subcategory-dropdown")) {
                        this.specialCaseEntity = new DeliverableSubcategorySelection(id, propertyName, additionalPickLists, displayName, description, categoryDropdownId, isRequired, isNew);
                    }
                    else if (this.type.includes("deliverable-stage-dropdown")) {
                        this.specialCaseEntity = new DeliverableStageSelection(id, propertyName, additionalPickLists, displayName, description, categoryDropdownId, isRequired, isNew);
                    }
                }
            }
        }

        if (this.type.includes("center-core-project-selection")) {
            let isNew = this.type.includes("new");
            let propNames: string[] = [];
            if (options[0]) {
                propNames = options[0];
            }
            this.specialCaseEntity = new CenterCoreProjectSelection(id, propNames, additionalPickLists, displayName, description, isNew);
        }

        if (this.type.includes("list-selection")) {
            let isNew = this.type.includes("new");
            let isNumber = this.type.includes("number");
            this.specialCaseEntity = new ListSelection(id, propertyName, displayName, description, isRequired, isNew, isNumber);
        }

        if (this.type.includes("file-upload")) {
            if (options.length > 0) {
                let fileUploadOptions: string[] = options[0];
                if (fileUploadOptions.length > 0) {
                    let rootFolderPath = fileUploadOptions[0];
                    if (this.type == "file-upload") {
                        this.specialCaseEntity = new FileUpload(id, propertyName, displayName, description, isRequired, rootFolderPath);
                    }
                    else if (this.type == "edit-file-upload") {
                        this.specialCaseEntity = new FileUpload(id, propertyName, displayName, description, isRequired, rootFolderPath, true);
                    }
                }
            }
        }

        if (this.type.includes("readonly")) {
            this.isReadOnly = true;
        }

        if (this.type.startsWith("input-url") || this.type == "input-email"|| this.type=="input-pmid") {
            this.alwaysValidate = true;
        }

        if (this.type.startsWith("filtered")) {
            if (options.length > 0) {
                let filterInfo: string[] = this.options[0];
                if (filterInfo.length > 1) {
                    this.filterParentId = filterInfo[0];
                    this.filterPropertyName = filterInfo[1];
                }
            }
            if (additionalPickLists.length > 1) {
                this.filterListName = additionalPickLists[0];
                this.filterListIdProperty = additionalPickLists[1];
            }
        }
    }

    generateHtml(displayFull: boolean = true): string {
        let requiredClass = "";
        if (this.isRequired) requiredClass = "required";

        let displaySize = "col-lg-5";
        if (displayFull) displaySize = "";

        let html = "";
        if (this.type == "input" || this.type == "input-email" || this.type =="input-pmid") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <input type="text" class="form-control ${displaySize} col-12 font-size14" id="${this.id}">
            </div>
            `
        }
        else if (this.type == "input-lg" || this.type.startsWith("input-url")) {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <textarea class="form-control ${displaySize} col-12 font-size14" rows="3" id="${this.id}"></textarea>
            </div>
            `
        }
        else if (this.type == "input-xl") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <textarea class="form-control col-12 font-size14" rows="5" id="${this.id}"></textarea>
            </div>
            `
        }
        else if (this.type == "input-xxl") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <textarea class="form-control col-12 font-size14" rows="10" id="${this.id}"></textarea>
            </div>
            `
        }
        else if (this.type == "input-number") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <input type="number" class="form-control ${displaySize} col-12 font-size14" id="${this.id}">
            </div>
            `
        }
        else if (this.type.startsWith("input-limit")) {
            let charLimit = this.type.replace("input-limit-", "");

            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <textarea class="form-control col-12 font-size14 mb-1" rows="10" id="${this.id}" maxlength="${charLimit}"></textarea>
                <div class="font-italic font-size11 text-right">Character Limit: <span id="${this.id}-char-count">0</span>/${charLimit}</div>
            </div>
            `
        }
        else if (this.type == "input-readonly") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <input type="text" class="form-control ${displaySize} col-12 font-size14" id="${this.id}" readonly>
            </div>
            `
        }
        else if (this.type == "dropdown-readonly") {
            html = `
            <div id="${this.id}-container" class="mb-3 col-12 ${displaySize} px-0">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <select id="${this.id}" class="selectpicker dropdown list-management-dropdown" title="None Selected" data-width="100%" data-container="body" data-dropup-auto="false" data-size="5" disabled></select>
            </div>
            `
        }
        else if (this.type == "dropdown" || this.type == "dropdown-pl") {
            html = `
            <div id="${this.id}-container" class="mb-3 col-12 ${displaySize} px-0">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <select id="${this.id}" class="selectpicker dropdown list-management-dropdown" title="None Selected" data-width="100%" data-container="body" data-dropup-auto="false" data-size="5"></select>
            </div>
            `
        }
        else if (this.type == "dropdown-pl-search") {
            html = `
            <div id="${this.id}-container" class="mb-3 col-12 ${displaySize} px-0">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <select id="${this.id}" class="selectpicker dropdown list-management-dropdown" title="None Selected" data-width="100%" data-container="body" data-dropup-auto="false" data-size="5" data-live-search="true"></select>
            </div>
            `
        }
        else if (this.type == "multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl") {
            html = `
            <div id="${this.id}-container" class="mb-3 col-12 ${displaySize} px-0">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mt-0 mb-1'>${this.description}</p>` : ``}
                <p class='form-text mb-1 mt-0 font-size12 font-italic'>Select all that apply.</p>
                <select id="${this.id}" class="selectpicker dropdown list-management-dropdown" title="None Selected" data-width="100%" data-container="body" data-dropup-auto="false" data-size="5" multiple></select>
            </div>
            `
        }
        else if (this.type == "multi-dropdown-pl-search" || this.type == "filtered-multi-dropdown-pl-search") {
            html = `
            <div id="${this.id}-container" class="mb-3 col-12 ${displaySize} px-0">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mt-0 mb-1'>${this.description}</p>` : ``}
                <p class='form-text mb-1 mt-0 font-size12 font-italic'>Select all that apply.</p>
                <select id="${this.id}" class="selectpicker dropdown list-management-dropdown" title="None Selected" data-width="100%" data-container="body" data-dropup-auto="false" data-size="5" data-live-search="true" multiple></select>
            </div>
            `
        }
        else if (this.type == "yes-no") {
            html = `
            <div id="${this.id}-container" class="mb-3 col-12 ${displaySize} px-0">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <select id="${this.id}" class="selectpicker dropdown list-management-dropdown" title="None Selected" data-width="100%" data-container="body" data-dropup-auto="false" data-size="5"></select>
            </div>
            `
        }
        else if (this.type == "date-input") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <input type="date" class="form-control ${displaySize} col-12 font-size14" id="${this.id}">
            </div>
            `
        }
        else if (this.type == "date-input-readonly") {
            html = `
            <div id="${this.id}-container" class="form-group">
                <label for="" class="${requiredClass}">${this.displayName}</label>
                ${this.description != "" ? `<p class='form-text mb-1 mt-0'>${this.description}</p>` : ``}
                <input type="date" class="form-control ${displaySize} col-12 font-size14" id="${this.id}" readonly>
            </div>
            `
        }

        // Special Cases
        else if (this.specialCaseEntity) {
            html = this.specialCaseEntity.generateHtml();
        }


        return html;
    }

    initialize(pickLists: JsonItem[], entityArr: any[]) {
        if (this.type == "dropdown") {
            this.generateDropdown();
        }
        else if (this.type == "multi-dropdown-pl" || this.type == "multi-dropdown-pl-search" || this.type == "dropdown-pl" || this.type == "dropdown-pl-search" || this.type == "dropdown-readonly" || this.type == "filtered-multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl-search" ) {
            // Create Options list
            let foundList = pickLists.find(list => list.Id == this.pickListName);
            if (foundList) {
                let foundPickList: PickListItem[] = foundList.Json;
                this.storedPickList = foundPickList;
                let optionsArr: string[][] = [];
                foundPickList.forEach(item => optionsArr.push([item.Value, item.Id.toString()]));
                this.options = optionsArr;
                this.generateDropdown();
            }
        }       
        else if (this.type == "yes-no") {
            this.options = [["Yes", "1"], ["No", "0"]]
            this.generateDropdown();
        }
        else if (this.type.startsWith("input-limit")) {
            $(`#${this.id}`).on("keyup change", () => {
                let currentInput: string =  <string>$(`#${this.id}`).val();
                let currentCharCount = currentInput.length;
                $(`#${this.id}-char-count`).text(currentCharCount);
            })
        }

        // Special Cases
        else if (this.specialCaseEntity) {
            this.specialCaseEntity.initialize(pickLists, entityArr);
        }


        if (this.type == "filtered-multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl-search") {
            $(`#${this.filterParentId}`).on("changed.bs.select", () => {
                let targetVal = <string>$(`#${this.filterParentId}`).val();
                let foundList = pickLists.find(list => list.Id == this.filterListName);
                if (foundList) {
                    let foundItemList: any[] = foundList.Json;
                    let filteredIdList: number[] = foundItemList.filter(item => (<number>_.get(item, this.filterPropertyName)).toString() == targetVal).map(item => <number>_.get(item, this.filterListIdProperty));
                    let filteredPickList = this.storedPickList.filter(item => filteredIdList.includes(item.Id));
                    let optionsArr: string[][] = [];
                    filteredPickList.forEach(item => optionsArr.push([item.Value, item.Id.toString()]));
                    this.options = optionsArr;
                    this.generateDropdown();

                    if (this.initialValue) {
                        // if all values match => populate value
                        if (this.storedValues.every((val, i, arr) => val == arr[0]) && this.storedValues.length > 0) {
                            this.populateField(this.storedValues[0]);
                        }
                    }
                }
            });

            $(`#${this.filterParentId}`).trigger("changed.bs.select");
        }

        this.initialValue = undefined;
        this.updateStoredValues(entityArr);
    }

    generateDropdown(): void {
        // Remove any previous options
        $(`#${this.id}`).find("option").remove();

        $(`#${this.id}`).selectpicker({
            dropupAuto: false,
            selectedTextFormat: "count",

            countSelectedText: (numSelected, numTotal) => {
                if (numSelected == numTotal) {
                    return "All";
                }
                else {
                    return numSelected + " Selected";
                }
            }
        });
        if (this.options != undefined) {
            if (this.type != "yes-no") {
                this.options.sort((x1: string[], x2: string[]) => {
                    if (x1[0].startsWith("-") && !x2[0].startsWith("-")) {
                        return 1;
                    }
                    else if (x2[0].startsWith("-") && !x1[0].startsWith("-")) {
                        return -1;
                    }
                    else {
                        return x1[0].localeCompare(x2[0]);
                    }
                });
            }
            this.options.forEach((element: string[]) => {
                $(`#${this.id}`).append(new Option(element[0], element[1], false, false))
            })
        }

        $(`#${this.id}`).selectpicker("refresh");
    }

    getValue(): any {
        if (this.type == "input" || this.type == "input-lg" || this.type == "input-xl" || this.type == "input-xxl" || this.type.startsWith("input-limit") || this.type.startsWith("input-url") || this.type == "input-email"|| this.type == "input-pmid") {
            return $(`#${this.id}`).val()?.toString().trim();
        }
        else if (this.type == "dropdown-pl" || this.type == "dropdown-pl-search" || this.type == "dropdown-readonly") {
            let stringVal = <string>$(`#${this.id}`).val();
            let parsedNum = parseInt(stringVal);
            if (!parsedNum) {
                return 0;
            }
            return parsedNum;
        }
        else if (this.type == "input-number") {
            let stringVal = <string>$(`#${this.id}`).val();
            if (stringVal) {
                return parseFloat(stringVal);
            }
            else {
                return null;
            }
        }
        else if (this.type == "dropdown" || this.type == "input-number") {
            return $(`#${this.id}`).val()
        }
        else if (this.type == "date-input") {
            let currentVal = $(`#${this.id}`).val();
            if (currentVal) {
                return currentVal;
            }
            return "0001-01-01"; // Treat as NULL;
        }
        else if (this.type == "multi-dropdown-pl" || this.type == "multi-dropdown-pl-search" || this.type == "filtered-multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl-search") {
            return (<string[]>$(`#${this.id}`).val())?.join("|");
        }
        else if (this.type == "yes-no") {
            let stringVal = <string>$(`#${this.id}`).val();
            if (stringVal) {
                if (stringVal == "1") {
                    return true;
                }
                else {
                    return false;
                }
            }
            else {
                return false;
            }
        }

        // Special Cases
        else if (this.specialCaseEntity) {
            return this.specialCaseEntity.getValue();
        }

        // Default
        else {
            return null;
        }
    }

    validate(): boolean {
        if (this.alwaysValidate) {
            if (this.type.startsWith("input-url")) {
                let val: string = this.getValue();
                if (!val && !this.isRequired) {
                    this.displayValid(true);
                    return true;
                }
                else if (this.type == "input-url-orcid") {
                    if (!val.startsWith("https://") || val.includes(" ") || !val.includes("orcid.org")) {
                        this.displayValid(false);
                        return false;
                    }
                    else {
                        this.displayValid(true);
                        return true;
                    }
                }
                else if (this.type == "input-url-linkedin") {
                    if (!val.startsWith("https://") || val.includes(" ") || !val.includes("linkedin.com")) {
                        this.displayValid(false);
                        return false;
                    }
                    else {
                        this.displayValid(true);
                        return true;
                    }
                }
                else {
                    if (!val.startsWith("https://") || val.includes(" ")) {
                        this.displayValid(false);
                        return false;
                    }
                    else {
                        this.displayValid(true);
                        return true;
                    }
                }
            }
            else if (this.type == "input-pmid") {
                let val: string = this.getValue();
                let trimmedVal = (val || "").trim();

                if (!trimmedVal) {
                    if (!this.isRequired) {
                        this.displayValid(true);
                        return true;
                    }

                    this.displayValid(false);
                    return false;
                }

                if (!/^\d{8}$/.test(trimmedVal)) {
                    this.displayValid(false);
                    return false;
                }

                this.displayValid(true);
                return true;
            }

            else if (this.type == "input-email") {
                let val: string = this.getValue();
                if (!val && !this.isRequired) {
                    this.displayValid(true);
                    return true;
                }
                else {
                    let trimmedVal = val.trim();
                    if (!trimmedVal.includes("@") || trimmedVal.includes(" ")) {
                        this.displayValid(false);
                        return false;
                    }
                    else {
                        this.displayValid(true);
                        return true;
                    }
                }
            }
        }

        if (this.isReadOnly) {
            return true;
        }

        // if its not required, its always valid
        if (!this.isRequired) {
            this.displayValid(true);
            return true;
        }
        if (this.specialCaseEntity) {
            return this.specialCaseEntity.validate();
        }
        // if you are editing more than 1 item, its always valid
        if (this.storedValues.length > 1) {
            this.displayValid(true);
            return true;
        }

        else if (this.type == "dropdown-pl" || this.type == "dropdown-pl-search") {
            if (this.getValue() != 0) {
                this.displayValid(true);
                return true;
            }
            else {
                this.displayValid(false);
                return false;
            }
        }
        else if (this.type == "input-number") {
            if (this.getValue() != null) {
                this.displayValid(true);
                return true;
            }
            else {
                this.displayValid(false);
                return false;
            }
        }
        else if (this.type == "yes-no") {
            if (this.getValue() != undefined) {
                this.displayValid(true);
                return true;
            }
            else {
                this.displayValid(false);
                return false;
            }
        }
        else if (this.type == "date-input") {
            if (this.getValue() != "0001-01-01") {
                this.displayValid(true);
                return true;
            }
            else {
                this.displayValid(false);
                return false;
            }
        }
        else {
            if (this.getValue() != "") {
                this.displayValid(true);
                return true;
            }
            else {
                this.displayValid(false);
                return false;
            }
        }
    }

    displayValid(isValid: boolean): void {
        if (this.type == "input" || this.type == "input-lg" || this.type == "input-xl" || this.type == "input-xxl" || this.type == "input-number" || this.type == "date-input" || this.type.startsWith("input-limit") || this.type.startsWith("input-url") || this.type == "input-email" || this.type =="input-pmid") {
            if (isValid) {
                $(`#${this.id}`).addClass("is-valid").removeClass("is-invalid");
            }
            else {
                $(`#${this.id}`).removeClass("is-valid").addClass("is-invalid");
            }
        }
        else if (this.type == "dropdown-pl" || this.type == "dropdown" || this.type == "dropdown-pl-search" || this.type == "multi-dropdown-pl" || this.type == "multi-dropdown-pl-search" || this.type == "yes-no" || this.type == "filtered-multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl-search") {
            if (isValid) {
                $(`#${this.id}`).siblings(".dropdown-toggle").addClass("is-valid").removeClass("is-invalid");
            }
            else {
                $(`#${this.id}`).siblings(".dropdown-toggle").removeClass("is-valid").addClass("is-invalid");
            }
        }

        // Special Cases
        else if (this.specialCaseEntity) {
            
        }
    }

    updateStoredValues(entityArray: any[]): void {
        if (this.specialCaseEntity) {
            this.specialCaseEntity.updateStoredValues(entityArray);
            return;
        }

        this.storedValues = [];

        entityArray.forEach(entity => {
            this.storedValues.push(_.get(entity, this.propertyName));
        })

        // if all values match => populate value
        if (this.storedValues.every((val, i, arr) => val == arr[0]) && this.storedValues.length > 0) {
            this.populateField(this.storedValues[0]);
        }

        if (this.initialValue == undefined) {
            this.setInitialValue();
        }
    }

    setInitialValue() {
        if (this.specialCaseEntity) {
            return;
        }

        this.initialValue = this.getValue();
    }

    populateField(value: any): void {
        if (this.type == "input" || this.type == "input-lg" || this.type == "input-number" || this.type == "input-xl" || this.type == "input-xxl" || this.type == "input-readonly" || this.type.startsWith("input-url") || this.type == "input-email"|| this.type =="input-pmid") {
            $(`#${this.id}`).val(value);
        }
        else if (this.type.startsWith("input-limit")) {
            $(`#${this.id}`).val(value);
            $(`#${this.id}`).trigger("change");
        }
        else if (this.type == "dropdown" || this.type == "dropdown-pl" || this.type == "dropdown-pl-search" || this.type == "dropdown-readonly") {
            $(`#${this.id}`).selectpicker("val", value);
        }
        else if (this.type == "multi-dropdown-pl" || this.type == "multi-dropdown-pl-search" || this.type == "filtered-multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl-search") {
            let optionArr = value?.split("|");
            $(`#${this.id}`).selectpicker("val", optionArr);
        }
        else if (this.type == "yes-no") {
            if (value == true) {
                $(`#${this.id}`).selectpicker("val", "1");
            }
            else {
                $(`#${this.id}`).selectpicker("val", "0");
            }
        }
        else if (this.type == "date-input" || this.type == "date-input-readonly") {
            if (!value) { return }
            let dateVal = new Date(value);
            let formattedVal = dateVal.toISOString().split("T")[0];
            // Don't populate if its default year (year 1)
            if (dateVal.getFullYear() > 1980) {
                $(`#${this.id}`).val(formattedVal);
            }
            else {
                $(`#${this.id}`).val();
            }
        }
    }

    checkChanged(): boolean {
        if (this.specialCaseEntity) {
            return this.specialCaseEntity.checkChanged();
        }

        let newVal = this.getValue();      
        return newVal != this.initialValue;
    }

    getDisplayValue(indexVal = -1): string {
        let useCurrent: boolean = indexVal < 0;
        let defaultDisplay = "-- Unknown --";
        if (this.type == "input" || this.type == "input-lg" || this.type == "input-xl" || this.type == "input-xxl" || this.type == "input-number" || this.type == "dropdown" || this.type.startsWith("input-limit") || this.type.startsWith("input-url") || this.type == "input-email"|| this.type== "input-pmid") {
            if (useCurrent) {
                return this.getValue();
            }
            else {
                let storedVal = this.storedValues[indexVal];
                if (storedVal) {
                    return storedVal;
                }
                else {
                    return ""
                }
            }
        }
        else if (this.type == "date-input") {
            if (useCurrent) {
                return this.getValue();
            }
            else {
                let dateVal = new Date(this.storedValues[indexVal]);
                let formattedVal = dateVal.toISOString().split("T")[0];
                return formattedVal;
            }
        }
        else if (this.type == "dropdown-pl" || this.type == "dropdown-pl-search") {
            let valueId = this.getValue();
            if (!useCurrent) {
                valueId = this.storedValues[indexVal];
            }
            let value = this.storedPickList.find(item => item.Id == valueId);
            if (value != undefined) {
                return value.Value;
            }
        }
        else if (this.type == "multi-dropdown-pl" || this.type == "multi-dropdown-pl-search" || this.type == "filtered-multi-dropdown-pl" || this.type == "filtered-multi-dropdown-pl-search") {
            let valueString = this.getValue();
            if (!useCurrent) {
                valueString = this.storedValues[indexVal];
            }
            let value = valueString?.split("|").map(itemId => {
                let value = this.storedPickList.find(item => item.Id.toString() == itemId);
                if (value) {
                    return value.Value;
                }
                else {
                    return defaultDisplay
                }
            }).join(", ");
            if (value) {
                return value;
            }
        }
        else if (this.type == "yes-no") {
            let valueBool = this.getValue();
            if (!useCurrent) {
                valueBool = this.storedValues[indexVal];
            }
            if (valueBool) {
                return "Yes";
            }
            else {
                return "No";
            }
        }

        return defaultDisplay;
    }
}

export interface SpecialCaseEntity {
    generateHtml(): string;
    initialize(pickLists: JsonItem[], entityArr: any[]): void;
    getValue(): any;
    validate(): boolean;
    updateStoredValues(entityArray: any[]): void;
    checkChanged(): boolean;
}